import { KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './BundleInspectorPanelComponent.css';
import ICoreBundle from '../../interfaces/ICoreBundle';
import { BundleElement } from '../../store/elementBundlesStore';
import { useTenantSettingsStore } from '../../store/tenantSettingsStore';
import { getBundleQuantityParts } from '../../utils/bundleQuantity';
import { formatBundlePrice } from '../../utils/bundlePrice';
import MoreMenuComponent from './MoreMenuComponent';
import { getSceneObjectStyle, SceneObjectStyleEnum } from '../../model/sceneObject/SceneObjectStyle';

// The selected bundle's colour in the scene, as a key next to its name
export const SELECTED_COLOR = `#${getSceneObjectStyle(SceneObjectStyleEnum.Selected).color.toString(16).padStart(6, '0')}`;

type Tab = 'overview' | 'elements';
const TABS: Tab[] = ['overview', 'elements'];

// The selection states that get a pill, each with its own icon so the states never depend on colour alone
const STATE_ICONS: Record<string, string> = {
    OPEN: "M8 4.25a3.75 3.75 0 1 0 0 7.5a3.75 3.75 0 1 0 0-7.5z",
    LOCKED: "M5 7V5.2a3 3 0 0 1 6 0V7M3.75 7h8.5v6.25h-8.5z",
    CONFIRMED: "M3.5 8.5l3 3 6-7",
};

const Icon = ({ path }: { path: string }) =>
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={path} />
    </svg>;

// A label, not a button
const StatePill = ({ state }: { state: string }) => {
    const { t } = useTranslation();
    const label = t(`command.bundleInspector.panel.states.${state.toLowerCase()}`);
    const prefix = t('command.bundleInspector.panel.statePrefix');
    return (
        <span className={`bundleInspectorPanel-state bundleInspectorPanel-state--${state.toLowerCase()}`} title={`${prefix} ${label}`}>
            <Icon path={STATE_ICONS[state]} />
            <span className="bundleInspectorPanel-srOnly">{prefix} </span>
            {label}
        </span>
    );
};

interface BundleInspectorPanelProps {
    bundles: ICoreBundle[];         // The pinned element's bundles; empty when it has none
    elements: BundleElement[];      // The elements of the bundle, when there is exactly one
    onClose: () => void;
    onDetachElements?: () => void;  // The "…" menu is shown only when detaching is possible
}

// The selected bundle's details, next to the running bundle inspector. Opens on Overview.
const BundleInspectorPanelComponent = ({ bundles, elements, onClose, onDetachElements }: BundleInspectorPanelProps) => {
    const { t } = useTranslation();
    const bundle = bundles.length === 1 ? bundles[0] : null;

    return (
        <div className="bundleInspectorPanel" role="dialog" aria-label={bundle?.name || t('command.bundleInspector.title')}>
            <Header bundle={bundle} title={bundles.length === 0 ? t('command.bundleInspector.noBundle') : null} elements={elements}
                    onClose={onClose} onDetachElements={onDetachElements} />
            {bundle && <BundleDetails bundle={bundle} elements={elements} />}
            {/* Being in several bundles is always a data error, so only the warning is shown */}
            {bundles.length > 1 &&
                <div className="bundleInspectorPanel-warning">
                    ⚠ {t('command.bundleInspector.multipleBundles', { count: bundles.length })}
                </div>}
        </div>
    );
};

interface HeaderProps {
    bundle: ICoreBundle | null;
    title: string | null;           // Shown when there is no single bundle
    elements: BundleElement[];
    onClose: () => void;
    onDetachElements?: () => void;
}

// One row: swatch · name · quantity · divider · "…" · ×. A long name is shortened; the quantity never wraps.
const Header = ({ bundle, title, elements, onClose, onDetachElements }: HeaderProps) => {
    const { t, i18n } = useTranslation();
    const quantity = bundle && getBundleQuantityParts(bundle.amount, bundle.amountUnit, i18n.language, () => t('command.bundleInspector.units.pcs'));
    const name = bundle?.name ?? title;

    return (
        <div className="bundleInspectorPanel-head">
            {bundle && <span className="bundleInspectorPanel-swatch" style={{ backgroundColor: SELECTED_COLOR }} aria-hidden="true" />}
            <h2 className="bundleInspectorPanel-title" title={name ?? undefined}>{name}</h2>
            {bundle?.status === "DISABLED" && <span className="bundleInspectorPanel-badge">{t('command.bundleInspector.panel.disabled')}</span>}
            {quantity &&
                <div className="bundleInspectorPanel-quantity">
                    <span className="bundleInspectorPanel-quantityValue">{quantity.value}</span>
                    {quantity.unit && <span className="bundleInspectorPanel-unit">{quantity.unit}</span>}
                </div>}
            {name && <span className="bundleInspectorPanel-headSep" aria-hidden="true" />}
            <span className="bundleInspectorPanel-headEnd">
                {bundle && onDetachElements && <DetachMenu bundle={bundle} elements={elements} onDetachElements={onDetachElements} />}
                <button type="button" className="bundleInspectorPanel-close" onClick={onClose}
                        aria-label={t('common.close')} title={t('common.close')}>
                    <Icon path="M4 4l8 8M12 4l-8 8" />
                </button>
            </span>
        </div>
    );
};

const DetachMenu = ({ bundle, elements, onDetachElements }: { bundle: ICoreBundle; elements: BundleElement[]; onDetachElements: () => void }) => {
    const { t } = useTranslation();
    const reason = elements.length === 0 ? 'noElements'
        : bundle.state === 'LOCKED' ? 'locked'
        : bundle.state === 'CONFIRMED' ? 'confirmed'
        : elements.length === 1 ? 'singleElement'   // A bundle must keep at least one element
        : null;

    return <MoreMenuComponent label={t('command.bundleInspector.panel.moreActions')} items={[{
        id: `detach-${bundle.id}`,
        label: t('command.bundleInspector.panel.detachElements'),
        disabled: reason !== null,
        hint: reason && t(`command.bundleInspector.panel.detachHints.${reason}`),
        onSelect: onDetachElements,
    }]} />;
};

interface BundleDetailsProps {
    bundle: ICoreBundle;
    elements: BundleElement[];
}

// The tabs under the header
const BundleDetails = ({ bundle, elements }: BundleDetailsProps) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState<Tab>('overview');
    const tabsRef = useRef<HTMLDivElement>(null);
    const uid = useId();

    const label = (key: string) => t(`command.bundleInspector.panel.${key}`);

    // ←/→ move and select, Home/End jump
    const onTabKeyDown = (e: KeyboardEvent) => {
        const i = TABS.indexOf(tab);
        const moves: Record<string, number> = { ArrowRight: (i + 1) % TABS.length, ArrowLeft: (i - 1 + TABS.length) % TABS.length, Home: 0, End: TABS.length - 1 };
        if (!(e.key in moves)) return;
        e.preventDefault();
        setTab(TABS[moves[e.key]]);
        tabsRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[moves[e.key]]?.focus();
    };

    return <>
        <div className="bundleInspectorPanel-tabsRow">
            <div ref={tabsRef} className="bundleInspectorPanel-tabs" role="tablist" aria-label={label('tabs')} onKeyDown={onTabKeyDown}>
                {TABS.map(id =>
                    <button key={id} type="button" role="tab" id={`${uid}-tab-${id}`} aria-selected={id === tab} aria-controls={`${uid}-panel`}
                            tabIndex={id === tab ? 0 : -1} className="bundleInspectorPanel-tab" onClick={() => setTab(id)}>
                        {label(id)}
                        {id === 'elements' && <span className="bundleInspectorPanel-count">{elements.length}</span>}
                    </button>)}
            </div>
        </div>
        <div id={`${uid}-panel`} role="tabpanel" tabIndex={0} aria-labelledby={`${uid}-tab-${tab}`} className="bundleInspectorPanel-tabPanel">
            {tab === 'overview' ? <Overview bundle={bundle} /> : <ElementList elements={elements} />}
        </div>
    </>;
};

// Empty optional fields are left out
const Overview = ({ bundle }: { bundle: ICoreBundle }) => {
    const { t, i18n } = useTranslation();
    const currencyCode = useTenantSettingsStore((s) => s.settings?.currencyCode ?? null);

    const material = bundle.selectedMaterial;
    const price = bundle.cost ? formatBundlePrice(bundle.cost, currencyCode, i18n.language) : null;
    const state = bundle.state && STATE_ICONS[bundle.state] ? bundle.state : null;

    return <>
        {bundle.description && <p className="bundleInspectorPanel-description">{bundle.description}</p>}
        <div className="bundleInspectorPanel-material">
            {material?.imageUrl
                ? <img className="bundleInspectorPanel-image" src={material.imageUrl} alt={material.name ?? ""} />
                : <div className="bundleInspectorPanel-image bundleInspectorPanel-image--none" aria-hidden="true" />}
            <div className="bundleInspectorPanel-materialText">
                <div className="bundleInspectorPanel-eyebrow">{t('command.bundleInspector.panel.material')}</div>
                <div className="bundleInspectorPanel-materialName">{material?.name || t('command.bundleInspector.panel.noMaterial')}</div>
                {material?.manufacturer && <div className="bundleInspectorPanel-manufacturer">{material.manufacturer}</div>}
                {(price || state) &&
                    <div className="bundleInspectorPanel-materialFoot">
                        {price ? <span className="bundleInspectorPanel-price">{price}</span> : <span />}
                        {state && <StatePill state={state} />}
                    </div>}
            </div>
        </div>
    </>;
};

const ElementList = ({ elements }: { elements: BundleElement[] }) => {
    const { t } = useTranslation();
    if (elements.length === 0)
        return <p className="bundleInspectorPanel-empty">{t('command.bundleInspector.panel.noElements')}</p>;

    // CoreAPI has no per-element quantity, so rows show only the name and the room
    return <ul className="bundleInspectorPanel-elementList">
        {elements.map(e =>
            <li key={e.globalId} className="bundleInspectorPanel-elementRow">
                <span className="bundleInspectorPanel-elementName">{e.name ?? e.globalId}</span>
                {e.roomNames.length > 0 && <span className="bundleInspectorPanel-elementRoom">{e.roomNames.join(", ")}</span>}
            </li>)}
    </ul>;
};

export default BundleInspectorPanelComponent;
